import React, { useState, useEffect, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Toolbar, Sort, Filter } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ShiftTemplateDto } from '../../api/models/ShiftTemplateDto';
import type { ShiftTemplateEntryDto } from '../../api/models/ShiftTemplateEntryDto';
import type { CrudFormProps } from '../../components/CrudPage';
import { useErrorHandler } from '../../utils/errorHandler';
import ShiftEntryFormDialog from './ShiftEntryFormDialog';
import { resolveValidationMessage } from '../../utils/validationMessages';

// Type for shift template with string id for CrudPage compatibility
type ShiftTemplateWithStringId = Omit<ShiftTemplateDto, 'id'> & { id: string };

// Validation schema for shift template (simplified - entries are managed separately)
const shiftTemplateSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  description: z.string().optional()
});

type ShiftTemplateFormData = z.infer<typeof shiftTemplateSchema>;

const ShiftModelTemplateFormDialog: React.FC<CrudFormProps<ShiftTemplateWithStringId>> = ({ value, onClose, onSave }) => {
  const { t } = useTranslation(['common']);
  const [visible, setVisible] = useState(false);
  const [entries, setEntries] = useState<ShiftTemplateEntryDto[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<ShiftTemplateEntryDto | null>(null);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const { handleError } = useErrorHandler();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    // watch
  } = useForm<ShiftTemplateFormData>({
    resolver: zodResolver(shiftTemplateSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: ''
    }
  });

  // const watchedValues = watch();

  useEffect(() => {
    if (value) {
      reset({
        name: value.name || '',
        description: value.description || ''
      });
      setEntries(value.entries?.map((entry, index) => ({
        ...entry,
        position: index,
        daysDisplay: formatDays(entry)
      })) || []);
    } else {
      reset({
        name: '',
        description: ''
      });
      setEntries([]);
    }
    setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formatDays is a local function; including it would trigger on every render
  }, [value, reset]);

  // Validate that entries don't overlap for the same day of week
  const validateNoOverlappingEntries = (entriesToValidate: ShiftTemplateEntryDto[]): string | null => {
    if (!entriesToValidate || entriesToValidate.length === 0) {
      return null;
    }

    const dayNames = [
      t('shiftModelTemplate.form.monday'),
      t('shiftModelTemplate.form.tuesday'),
      t('shiftModelTemplate.form.wednesday'),
      t('shiftModelTemplate.form.thursday'),
      t('shiftModelTemplate.form.friday'),
      t('shiftModelTemplate.form.saturday'),
      t('shiftModelTemplate.form.sunday')
    ];

    // Check each day of week
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const entriesForDay = entriesToValidate.filter(entry => {
        switch (dayIndex) {
          case 0: return entry.monday === true;
          case 1: return entry.tuesday === true;
          case 2: return entry.wednesday === true;
          case 3: return entry.thursday === true;
          case 4: return entry.friday === true;
          case 5: return entry.saturday === true;
          case 6: return entry.sunday === true;
          default: return false;
        }
      });

      // Check for overlaps between all pairs of entries for this day
      for (let i = 0; i < entriesForDay.length; i++) {
        for (let j = i + 1; j < entriesForDay.length; j++) {
          const entry1 = entriesForDay[i];
          const entry2 = entriesForDay[j];
          
          if (entriesOverlap(entry1, entry2)) {
            return t('shiftModelTemplate.form.entriesOverlap', 
              `Die Schicht-Einträge '${entry1.shiftName}' und '${entry2.shiftName}' überlappen sich am ${dayNames[dayIndex]}. Einträge dürfen sich für denselben Wochentag nicht überlappen.`,
              { entry1: entry1.shiftName, entry2: entry2.shiftName, day: dayNames[dayIndex] }
            );
          }
        }
      }
    }

    return null;
  };

  // Check if two shift template entries overlap in time on the same calendar day.
  // Weekday refers to start time; overnight shifts (e.g. 21:00–03:30) end on the next day,
  // so we only consider the part of each shift on the start day for overlap.
  const entriesOverlap = (entry1: ShiftTemplateEntryDto, entry2: ShiftTemplateEntryDto): boolean => {
    if (!entry1.startTime || !entry1.endTime || !entry2.startTime || !entry2.endTime) {
      return false;
    }

    const start1 = parseTime(entry1.startTime);
    const end1 = parseTime(entry1.endTime);
    const start2 = parseTime(entry2.startTime);
    const end2 = parseTime(entry2.endTime);

    const periods1 = getAvailablePeriodsForOverlapCheck(
      start1, end1,
      entry1.breakStart ? parseTime(entry1.breakStart) : null,
      entry1.breakEnd ? parseTime(entry1.breakEnd) : null
    );
    const periods2 = getAvailablePeriodsForOverlapCheck(
      start2, end2,
      entry2.breakStart ? parseTime(entry2.breakStart) : null,
      entry2.breakEnd ? parseTime(entry2.breakEnd) : null
    );

    // Check if any period from entry1 overlaps with any period from entry2
    for (const period1 of periods1) {
      for (const period2 of periods2) {
        if (periodsOverlap(period1, period2)) {
          return true;
        }
      }
    }
    return false;
  };

  // Parse time string (HH:mm) to minutes since midnight
  const parseTime = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const MINUTES_PER_DAY = 24 * 60; // 1440

  // For overlap check: only the part on the start day. Overnight [00:00, end] is next day and is excluded.
  const getAvailablePeriodsForOverlapCheck = (
    start: number,
    end: number,
    breakStart: number | null,
    breakEnd: number | null
  ): Array<{ start: number; end: number }> => {
    if (end < start) {
      return [{ start, end: MINUTES_PER_DAY }];
    }
    return getAvailablePeriods(start, end, breakStart, breakEnd);
  };

  // Get available time periods for a shift entry, accounting for breaks and midnight crossover
  const getAvailablePeriods = (
    start: number,
    end: number,
    breakStart: number | null,
    breakEnd: number | null
  ): Array<{ start: number; end: number }> => {
    const periods: Array<{ start: number; end: number }> = [];

    if (end < start) {
      // Midnight crossover: split into [start, 24:00] and [00:00, end]
      periods.push({ start, end: MINUTES_PER_DAY });
      periods.push({ start: 0, end });
    } else if (breakStart !== null && breakEnd !== null &&
        breakStart >= start && breakEnd <= end && breakStart < breakEnd) {
      // There's a break, so we have two periods
      if (start < breakStart) {
        periods.push({ start, end: breakStart });
      }
      if (breakEnd < end || breakEnd === end) {
        periods.push({ start: breakEnd, end });
      }
    } else {
      // No break or invalid break, single period
      periods.push({ start, end });
    }

    return periods;
  };

  // Check if two time periods overlap
  const periodsOverlap = (period1: { start: number; end: number }, period2: { start: number; end: number }): boolean => {
    // Two periods overlap if one starts before the other ends
    return period1.start < period2.end && period2.start < period1.end;
  };

  const onSubmit = async (data: ShiftTemplateFormData) => {
    try {
      // Validate entries before saving
      const validationError = validateNoOverlappingEntries(entries);
      if (validationError) {
        handleError(new Error(validationError), 'validating shift template entries');
        return;
      }

      const shiftTemplate: ShiftTemplateWithStringId = {
        ...value,
        id: value?.id || '',
        name: data.name,
        description: data.description,
        entries: entries.map((entry, index) => ({
          ...entry,
          position: index
        }))
      };
      
      await onSave(shiftTemplate);
      setVisible(false);
    } catch (error) {
      handleError(error, 'saving shift template');
    }
  };

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  const handleAddEntry = () => {
    setSelectedEntry(null);
    setEditMode(false);
    setEntryDialogOpen(true);
  };

  const handleEditEntry = () => {
    if (selectedEntry) {
      setEditMode(true);
      setEntryDialogOpen(true);
    }
  };

  const handleDeleteEntry = () => {
    if (selectedEntry) {
      setEntries(prev => prev.filter(entry => entry !== selectedEntry));
      setSelectedEntry(null);
    }
  };

  const handleEntrySave = async (entry: ShiftTemplateEntryDto) => {
    // Add a computed field for days display
    const entryWithComputedDays = {
      ...entry,
      daysDisplay: formatDays(entry)
    };
    
    if (editMode && selectedEntry) {
      // Update existing entry
      setEntries(prev => prev.map(e => e === selectedEntry ? entryWithComputedDays : e));
    } else {
      // Add new entry
      setEntries(prev => [...prev, entryWithComputedDays]);
    }
    setEntriesError(null);
    setEntryDialogOpen(false);
    setSelectedEntry(null);
  };

  const handleEntryClose = () => {
    setEntryDialogOpen(false);
    setSelectedEntry(null);
  };

  // Helper function to format days
  const formatDays = (entry: ShiftTemplateEntryDto | undefined) => {
    if (!entry) return '';
    
    const days = [];
    if (entry.monday) days.push(t('shiftModelTemplate.form.monday'));
    if (entry.tuesday) days.push(t('shiftModelTemplate.form.tuesday'));
    if (entry.wednesday) days.push(t('shiftModelTemplate.form.wednesday'));
    if (entry.thursday) days.push(t('shiftModelTemplate.form.thursday'));
    if (entry.friday) days.push(t('shiftModelTemplate.form.friday'));
    if (entry.saturday) days.push(t('shiftModelTemplate.form.saturday'));
    if (entry.sunday) days.push(t('shiftModelTemplate.form.sunday'));
    
    const result = days.join(', ');
    return result;
  };

  const dialogContent = useCallback(() => {
    const toolbarClick = (args: any) => {
      if (!args.item) return;

      switch (args.item.text) {
        case t('actions.add'): handleAddEntry(); break;
        case t('actions.edit'): handleEditEntry(); break;
        case t('actions.delete'): handleDeleteEntry(); break;
      }
    };

    return (
      <>
        <div className="p-4 space-y-4">
          {/* Template Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.name')} *
            </label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  placeholder={t('shiftModelTemplate.form.namePlaceholder')}
                  cssClass={errors.name ? 'e-error' : ''}
                />
              )}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">
                {resolveValidationMessage(t, errors.name)}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('shiftModelTemplate.form.description')}
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextBoxComponent
                  {...field}
                  placeholder={t('shiftModelTemplate.form.descriptionPlaceholder')}
                  multiline={true}
                />
              )}
            />
          </div>

          {/* Shift Entries Grid */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('shiftModelTemplate.form.entries')} ({entries.length})
            </label>
            <div className="h-64">
              <GridComponent
                dataSource={entries}
                height="100%"
                allowSorting={true}
                allowFiltering={false}
                rowSelected={(e) => setSelectedEntry(e.data as ShiftTemplateEntryDto)}
                toolbar={[
                  { text: t('actions.add'), tooltipText: t('actions.add'), prefixIcon: 'e-add', id: 'add' },
                  { text: t('actions.edit'), tooltipText: t('actions.edit'), prefixIcon: 'e-edit', id: 'edit' },
                  { text: t('actions.delete'), tooltipText: t('actions.delete'), prefixIcon: 'e-delete', id: 'delete' }
                ]}
                toolbarClick={toolbarClick}
                locale="de-DE"
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="shiftName"
                    headerText={t('shiftModelTemplate.form.shiftName')}
                    width={150}
                  />
                  <ColumnDirective
                    field="startTime"
                    headerText={t('shiftModelTemplate.form.startTime')}
                    width={100}
                  />
                  <ColumnDirective
                    field="endTime"
                    headerText={t('shiftModelTemplate.form.endTime')}
                    width={100}
                  />
                  <ColumnDirective
                    field="breakStart"
                    headerText={t('shiftModelTemplate.form.breakStart')}
                    width={100}
                  />
                  <ColumnDirective
                    field="breakEnd"
                    headerText={t('shiftModelTemplate.form.breakEnd')}
                    width={100}
                  />
                  <ColumnDirective
                    field="daysDisplay"
                    headerText={t('shiftModelTemplate.form.daysOfWeek')}
                    width={200}
                  />
                </ColumnsDirective>
                <Inject services={[Toolbar, Sort, Filter]} />
              </GridComponent>
            </div>
            {entries.length === 0 && (
              <p className="text-gray-500 text-sm mt-2">
                {t('shiftModelTemplate.form.noEntries')}
              </p>
            )}
          </div>
        </div>
        {entriesError && (
          <p className="text-red-500 text-sm mt-2">{entriesError}</p>
        )}
      </>
    );
  }, [control, errors, t, entries, entriesError, selectedEntry, setSelectedEntry]);

  return (
    <>
      <DialogComponent
        width="1000px"
        isModal={true}
        visible={visible}
        close={handleClose}
        header={value ? t('shiftModelTemplate.form.editTitle') : t('shiftModelTemplate.form.createTitle')}
        footerTemplate={() => (
          <div className="flex justify-end space-x-2">
            <ButtonComponent
              type="button"
              onClick={handleClose}
              cssClass="e-outline"
            >
              {t('actions.cancel')}
            </ButtonComponent>
            <ButtonComponent
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              cssClass="e-primary"
            >
              {isSubmitting ? t('actions.saving') : t('actions.save')}
            </ButtonComponent>
          </div>
        )}
      >
        {dialogContent()}
      </DialogComponent>

      {entryDialogOpen && (
        <ShiftEntryFormDialog
          value={editMode ? selectedEntry : null}
          onClose={handleEntryClose}
          onSave={handleEntrySave}
        />
      )}
    </>
  );
};

export default ShiftModelTemplateFormDialog;
